<template>
    <div class="table-responsive">
        <!-- <h5>Write Offs</h5> -->
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th width="7%">Credit:</b-th>
                    <b-th width="10%">Reason:</b-th>
                    <b-th class="text-right" width="10%">Amount:</b-th>
                    <b-th class="text-right" width="10%">Balance:</b-th>
                    <b-th width="5%"></b-th>
                    <b-th width="30%">Notes:</b-th>
                    <b-th width="7%">Date:</b-th>
                    <b-th width="5%"></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="credit in credits" :key="credit.id">
                    <b-td class="text-nowrap">
                        <strong
                            v-if="getPermissionEdit(credit)"
                            class="text-primary cursor-pointer"
                            @click="showEditModal(credit)"
                            :class="
                                isHasUsersChooseInvoice
                                    ? 'disabled cursor-not-allowed'
                                    : 'cursor-pointer'
                            "
                        >
                            {{ credit.name }}
                        </strong>
                        <span v-else>{{ credit.name }}</span>
                    </b-td>
                    <b-td>
                        {{ getReason(credit) }}
                    </b-td>
                    <b-td class="text-right">{{
                        _toCurrency(credit.amount)
                    }}</b-td>
                    <b-td class="text-right">{{
                        _toCurrency(credit.balance)
                    }}</b-td>
                    <b-td></b-td>
                    <b-td v-html="convertNewLineToBreak(credit.note)"></b-td>
                    <b-td> {{ _carbonDateDisplay(credit.credit_date) }}</b-td>
                    <b-td class="text-right">
                        <span v-if="allowApplyOrCancelCredit(credit)">
                            <template v-if="credit.is_applied_to_invoice">
                                <b-button
                                    v-if="!credit.is_return_posted"
                                    right
                                    variant="link"
                                    size="sm"
                                    class="p-0"
                                    title="Cancel credit"
                                    :disabled="isHasUsersChooseInvoice"
                                    :class="{
                                        'cursor-not-allowed': isHasUsersChooseInvoice
                                    }"
                                    :style="
                                        isHasUsersChooseInvoice
                                            ? { 'pointer-events': 'all' }
                                            : ''
                                    "
                                    @click="cancelCredit(credit)"
                                >
                                    <i class="cus-arrow-undo" />
                                </b-button>
                            </template>
                            <template v-else>
                                <b-button
                                    right
                                    variant="link"
                                    size="sm"
                                    class="p-0"
                                    title="Apply credit"
                                    :disabled="isHasUsersChooseInvoice"
                                    :class="{
                                        'cursor-not-allowed': isHasUsersChooseInvoice
                                    }"
                                    :style="
                                        isHasUsersChooseInvoice
                                            ? { 'pointer-events': 'all' }
                                            : ''
                                    "
                                    @click="applyCredit(credit)"
                                >
                                    <i class="cus-accept" />
                                </b-button>
                            </template>
                        </span>
                    </b-td>
                </b-tr>
                <b-tr v-if="credits.length == 0 && !isLoading">
                    <b-td colspan="7"><i>No records available</i></b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && showPagination"
            v-show="meta.lastPage > 1"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            class="vet-pagination"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getCredits"
        />
        <modal-edit-credit
            modal-id="modal-edit-credit"
            ref="modal-edit-credit"
            :invoicePage="invoice.id"
            :client-name="selectedClient"
            @reload="reloadRelated()"
        />
        <modal-cancel-confirm-credit
            modal-id="modal-cancel-confirm-credit"
            ref="modal-cancel-confirm-credit"
            modalRef="modal-cancel-confirm-credit"
            @reload="reloadRelated()"
        />
    </div>
</template>

<script>
/**
 * @name CreditList
 * @description List of credits based on invoice
 * @see apps/application/views/staff/billing/invoice/open/sub/credit/list.php
 * @see apps/assets/js/module/billing/credit.js
 * @see apps/application/controllers/billing/credit.php
 * @see apps/application/views/staff/billing/sub/credit/edit_confirm.php
 */
import invoiceHelper from "@/mixins/invoice";
import Loader from "@/components/elements/loader-list";
import InvoiceCreditService from "@/services/invoice/invoice-credit";
import CBILLINGS from "@/constants/billings";
import ModalEditCredit from "@/components/billing-credit/credit-edit-modal";
import ModalCancelConfirmCredit from "@/components/invoice-credit/credit-cancel-confirm-modal.vue";
import { isEmpty } from "lodash";

export default {
    mixins: [invoiceHelper],
    components: {
        Loader,
        ModalEditCredit,
        ModalCancelConfirmCredit
    },
    props: {
        invoice: {
            type: Object,
            required: true
        }
    },
    computed: {
        isHasUsersChooseInvoice() {
            return !isEmpty(
                this.$store.getters["billing/findUserChooseInvoice"](
                    this.invoice.id
                )
            );
        }
    },
    data() {
        return {
            CBILLINGS: CBILLINGS,
            isLoading: false,
            showPagination: false,
            credits: [],
            meta: null,
            selectedClient: null,
            selectedCredit: { name: null }
        };
    },
    mounted() {
        this.getCredits();
    },
    methods: {
        getCredits(page = 1) {
            let self = this;
            this.isLoading = true;

            const params = {
                page: page,
                perPage: self.PER_PAGE
            };
            InvoiceCreditService.getCredits(this.invoice.id, params)
                .then(res => {
                    if (res) {
                        self.meta = self.standardizePagingKey(res);

                        if (self.meta.total > self.PER_PAGE) {
                            self.showPagination = true;
                        }

                        self.credits = res.data;
                    }
                    self.isLoading = false;
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to get credit list",
                        "danger"
                    );
                    this.isLoading = false;
                    console.error(error);
                });
        },
        reloadRelated() {
            this.getCredits(this.meta.currentPage);
            this.$emit("reload-invoice-details");
        },
        standardizePagingKey(res) {
            res.lastPage = res.last_page;
            res.currentPage = res.current_page;
            res.perPage = res.per_page;
            return res;
        },
        showEditModal(credit) {
            if (this.isHasUsersChooseInvoice) {
                return;
            }
            this.selectedCredit = credit;
            this.selectedClient = credit.client_name;
            let date = credit.credit_date
                ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                : null;

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs["modal-edit-credit"].open(credit),
                onError: message => this._message(message, "danger")
            });
        },
        getPermissionEdit(credit) {
            return (
                this._hasAccess("billing.credit.update") &&
                credit.is_active_actor &&
                credit.amount > 0 &&
                !credit.isEditable
            );
        },
        allowApplyOrCancelCredit(credit) {
            return (
                this.invoice.status != CBILLINGS.INVOICE.TYPE_REOPENED &&
                this._hasAccess("billing.invoice.credit.apply") &&
                credit.is_active_actor
            );
        },
        applyCredit(credit) {
            let date = credit.credit_date
                ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                : null;
            let params = {
                invoice_id: this.invoice.id,
                credit_id: credit.id
            };
            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () =>
                    InvoiceCreditService.applyCredit(params).then(async res => {
                        if (
                            res.code == 200 &&
                            res.message == "Credit applied"
                        ) {
                            this._message(res.message);
                            this.reloadRelated();
                        } else {
                            if (this.invoice.balance_amount > 0) {
                                //VET-4150 - force credit tab reload, regardless of invoice status
                                this.reloadRelated();
                            }
                            this._message(res.message, "danger");
                        }
                    }),
                onError: message => this._message(message, "danger")
            });
        },
        cancelCredit(credit) {
            let date = credit.credit_date
                ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                : null;
            let params = {
                invoice_id: this.invoice.id,
                credit_id: credit.id
            };
            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => {
                    if (
                        this.invoice.status == CBILLINGS.INVOICE.TYPE_WRITEOFF
                    ) {
                        this.$refs["modal-cancel-confirm-credit"].open(params);
                    } else if (!credit.is_return_posted) {
                        InvoiceCreditService.cancelCredit(params).then(res => {
                            if (res.code == 200) {
                                this._message(res.message);
                                this.reloadRelated();
                            } else {
                                this._message(res.message, "danger");
                            }
                        });
                    }
                },
                onError: message => this._message(message, "danger")
            });
        },
        getReason(credit) {
            let reason = null;
            if (credit.reason) return credit.reason;
            if (credit.type)
                return credit.type in CBILLINGS.CREDIT_TYPE
                    ? CBILLINGS.CREDIT_TYPE[credit.type]
                    : reason;
        }
    }
};
</script>
